import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, Field, HStack, Text, VStack} from '@chakra-ui/react3'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import ProductDisplayPrice from '../../../../components/product-display-price'
import useAR from '../../../../hooks/use-ar'
import {useConfiguratorHighlights} from '../../../../hooks/useScapiCustomApi'
import {_3DViewIcon, ResourcesAddIcon, TextCircleIcon} from '../../../../leap/components/icons'
import {
    findMainSetProduct,
    getMasterProductId,
    isProductOrderable,
    isProductSet
} from '../../../../utils/product-utils'
import BadgeGroup from '../../../../components/badge-group'
import ProductAvailability from '../../../../leap/components/product-availability/index.jsx'
import PromotionBanner from '../../../../leap/components/promotion-banner'
import Gallery from './gallery'
import DisplayPrice from '../../../../leap/components/display-price/index.jsx'
import AgeRange from '../../../../leap/components/age-range'
import CockpitCtaSection from './cockpit-cta-section'
import EstimatedDelivery from '../../../../leap/components/estimated-delivery'
import ColorSwatches from '../../../../leap/components/color-swatches'
import logger from '../../../../utils/logger-instance'
import {MAIN_IMAGES_IMAGE_TYPE} from '../../../../constants'
import {useQuickview} from '../../../../partials/xpdp-configurator/partials/quickview'
import CmsModules from '../../../../leap/modules'

const HighlightItem = ({children}) => (
    <HStack
        gap="spacing-12"
        align="center"
        color="text.primary"
        textStyle="heading.small"
        _icon={{width: '1rem', height: '1rem'}}
    >
        {children}
    </HStack>
)
HighlightItem.propTypes = {
    children: PropTypes.node
}

const UNIVERSAL_SWATCH_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26"><circle cx="13" cy="13" r="13" fill="white"/></svg>'
)}`

/**
 * Return a swatch-ready product for the cockpit universal color fallback.
 *
 * When an xpdp product has no color variation, this creates a synthetic single-option
 * color variation so the cockpit can still render through ColorSwatches.Root.
 *
 * @param {Object} product
 * @param {Function} formatMessage
 * @returns {Object}
 */
const getSwatchProduct = (product, formatMessage) => {
    const hasColorVariation = product?.variationAttributes?.some(
        ({id, values}) => id === 'color' && values?.length
    )

    if (hasColorVariation) {
        // we have product color then not need to fallback to universal one
        return product
    }

    const universalLabel = formatMessage({
        id: 'product_detail.color.universal',
        defaultMessage: 'Universal'
    })
    const colorLabel = formatMessage({
        id: 'product_detail.color',
        defaultMessage: 'Color'
    })
    const orderable = isProductOrderable(product)
    const variationValues = {...product?.variationValues, color: 'universal'}

    return {
        ...product,
        variationValues,
        variationAttributes: [
            ...(product?.variationAttributes?.filter(({id}) => id !== 'color') || []),
            {
                id: 'color',
                name: colorLabel,
                values: [{name: universalLabel, value: 'universal', orderable}]
            }
        ],
        variants: [
            ...(product?.variants || []),
            {
                productId: product.id,
                variationValues,
                orderable
            }
        ],
        imageGroups: [
            ...(product?.imageGroups || []),
            {
                viewType: 'swatch',
                variationAttributes: [{id: 'color', values: [{value: 'universal'}]}],
                images: [
                    {
                        alt: universalLabel,
                        title: universalLabel,
                        disBaseLink: UNIVERSAL_SWATCH_IMAGE,
                        link: UNIVERSAL_SWATCH_IMAGE
                    }
                ]
            }
        ]
    }
}

const SetProductSwatches = ({universalSwatchEnabled, product, productShortNamePlacement}) => {
    const {formatMessage} = useIntl()

    const swatchProduct = useMemo(
        () => (universalSwatchEnabled ? getSwatchProduct(product, formatMessage) : product),
        [universalSwatchEnabled, formatMessage, product]
    )

    const isSelectedSwatchUnavailable = useMemo(() => !isProductOrderable(product), [product])

    return (
        <Field.Root invalid={isSelectedSwatchUnavailable}>
            <ColorSwatches.Root
                asChild
                product={swatchProduct}
                productShortNamePlacement={productShortNamePlacement}
            />
            {isSelectedSwatchUnavailable && (
                <Field.ErrorText>
                    <FormattedMessage
                        id="configurator.line_item.error.color_not_available"
                        defaultMessage="The selected colour is currently out of stock. Please select a different colour."
                    />
                </Field.ErrorText>
            )}
        </Field.Root>
    )
}
SetProductSwatches.propTypes = {
    universalSwatchEnabled: PropTypes.bool,
    product: PropTypes.object,
    productShortNamePlacement: PropTypes.oneOf(['auto', 'only', 'before'])
}

const XleapCockpit = ({
    derivedProduct,
    product,
    isGetPropsLoading,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    onRatingsClick,
    viewModel
}) => {
    const {formatMessage} = useIntl()
    const configuratorConfig = product?.c_configuratorConfig
    const displayTemplate = product?.c_displayTemplate
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {ARModel, hotspots} = derivedProduct
    const ARProps = useAR(ARModel)
    const mainHotspots = useMemo(() => hotspots?.[MAIN_IMAGES_IMAGE_TYPE], [hotspots])
    const [hotspotProduct, setHotspotProduct] = useState(null)
    const {open: openQuickview, close: closeQuickview} = useQuickview()
    const clearHotspot = useCallback(() => setHotspotProduct(null), [])
    const onHotspotClick = useCallback(
        ({pid}) => {
            const product = {id: pid}
            setHotspotProduct(product)
            openQuickview({
                product,
                onAddToCart: () => {
                    clearHotspot()
                    closeQuickview()
                },
                onClose: clearHotspot
            })
        },
        [openQuickview, closeQuickview, clearHotspot]
    )
    const highlightsQuery = useConfiguratorHighlights(
        {
            parameters: {
                configId: configuratorConfig,
                productId: getMasterProductId(product) || product?.id
            }
        },
        {
            enabled: displayTemplate === 'xpdp-configurator'
        }
    )
    const hasHighlights =
        highlightsQuery.data?.numberOfColors > 0 ||
        highlightsQuery.data?.numberOfAccessories > 0 ||
        highlightsQuery.data?.hasEngraving

    useEffect(() => {
        if (displayTemplate === 'xpdp-configurator' && !product?.c_colorProduct) {
            logger.error(`"${product.id}" has no colorProduct configured in configurator config`)
        }
    }, [displayTemplate, product?.c_colorProduct, product?.id])

    const mainSetProduct = useMemo(
        () =>
            displayTemplate === 'xpdp-configurator' &&
            (isProductSet(product) ? findMainSetProduct(product) : product),
        [product, displayTemplate]
    )

    return (
        <Box paddingInline={{base: 0, s: 'spacing-92'}}>
            <Box
                data-testid={`cockpit-${displayTemplate}`}
                display={{
                    base: 'flex',
                    s: 'grid'
                }}
                flexDirection="column"
                paddingTop={{base: 'spacing-16', s: 'spacing-32'}}
                paddingBottom={{base: 0, s: 'spacing-64'}}
                gapX={{base: 'spacing-32', s: 'spacing-80'}}
                align={{base: 'stretch', s: 'start'}}
                layerStyle="container.page"
                gridTemplateAreas='"gallery details"'
                gridTemplateColumns="2fr 1fr"
                gridTemplateRows="auto auto"
            >
                <Box
                    gridArea="gallery"
                    position="sticky"
                    top={{base: 'spacing-8', s: 'spacing-32'}}
                    paddingTop="spacing-8"
                    paddingBottom="spacing-24"
                    zIndex="sticky"
                    alignSelf={{base: 'stretch', s: 'start'}}
                    height="fit-content"
                    bg={{base: 'bg', s: 'transparent'}}
                    role="group"
                    // This pseudoelement is used to hide the extra space above the gallery introduced by the value of the top position property
                    _before={{
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: {base: '-spacing-8', s: '-spacing-32'},
                        height: {base: 'spacing-8', s: 'spacing-32'},
                        backgroundColor: 'bg',
                        pointerEvents: 'none'
                    }}
                    aria-label={formatMessage(
                        {
                            id: 'product_detail.product_name_carousel',
                            defaultMessage: '{productName} carousel'
                        },
                        {productName: product.name}
                    )}
                >
                    <Gallery
                        product={product}
                        derivedProduct={derivedProduct}
                        isMasterLoading={isMasterLoading}
                        isVariantLoading={isVariantLoading}
                        hotspots={mainHotspots}
                        onHotspotClick={onHotspotClick}
                        activePid={hotspotProduct?.id}
                    />
                </Box>

                <Box gridArea="details" minWidth={0}>
                    <VStack gap="spacing-32" align="stretch">
                        <Box data-testid="main-info" paddingInline={{base: 'spacing-24', s: 0}}>
                            <VStack gap={{base: 'spacing-20', s: 'spacing-24'}} align="stretch">
                                <VStack gap="spacing-4" align="stretch">
                                    <Box>
                                        <Box float="right">
                                            <BadgeGroup product={product} />
                                        </Box>
                                        <Box>
                                            <AgeRange
                                                product={product}
                                                variant="configurator-quickview"
                                            />
                                        </Box>
                                        <Text
                                            as="h1"
                                            paddingTop={{base: 'spacing-20', s: 'spacing-24'}}
                                        >
                                            <Text
                                                as="span"
                                                display="inline-block"
                                                color="text.primary"
                                                textStyle="display.small"
                                            >
                                                {product.name}
                                            </Text>
                                        </Text>
                                    </Box>

                                    <Text
                                        as="span"
                                        display="inline-block"
                                        color="text.tertiary"
                                        textStyle="body.medium"
                                    >
                                        {product.c_slogan}
                                    </Text>
                                </VStack>

                                {hasProductRating && (
                                    <BVProductRating
                                        product={product}
                                        variant="cockpit"
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                                <PromotionBanner product={product} />
                            </VStack>
                        </Box>

                        {product?.c_pdpCockpitContent && (
                            <Box paddingInline={{base: 'spacing-24', s: 0}}>
                                <CmsModules assetId={product.c_pdpCockpitContent} />
                            </Box>
                        )}

                        <VStack
                            gap="spacing-24"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                        >
                            {!isProductSet(product) ? (
                                <SetProductSwatches
                                    product={product}
                                    productShortNamePlacement="auto"
                                />
                            ) : displayTemplate === 'xpdp' ? (
                                product.setProducts.map((setProduct) => (
                                    <SetProductSwatches
                                        key={setProduct.setProductId}
                                        universalSwatchEnabled
                                        productShortNamePlacement="before"
                                        product={setProduct}
                                    />
                                ))
                            ) : displayTemplate === 'xpdp-configurator' && mainSetProduct ? (
                                <SetProductSwatches
                                    product={mainSetProduct}
                                    productShortNamePlacement="auto"
                                />
                            ) : null}

                            <Text color="text.secondary" textStyle="body.small">
                                {product.shortDescription}
                            </Text>

                            {displayTemplate === 'xpdp-configurator' && hasHighlights && (
                                <VStack gap="spacing-12" align="stretch">
                                    {highlightsQuery.data?.numberOfAccessories > 0 && (
                                        <HighlightItem>
                                            <ResourcesAddIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.number_of_accessories"
                                                defaultMessage="Customise with {value} more accessories"
                                                values={{
                                                    value: highlightsQuery.data?.numberOfAccessories
                                                }}
                                            />
                                        </HighlightItem>
                                    )}

                                    {highlightsQuery.data?.hasEngraving && (
                                        <HighlightItem>
                                            <TextCircleIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.has_engraving"
                                                defaultMessage="Free engraving"
                                            />
                                        </HighlightItem>
                                    )}
                                </VStack>
                            )}

                            {ARProps?.hasAR && (
                                <Button
                                    as="a"
                                    variant="secondaryOutline"
                                    display={{base: 'inline-flex', s: 'none'}}
                                    href={ARProps?.href || '#'}
                                    rel="ar"
                                    onClick={ARProps?.onARLinkClick}
                                    aria-label={formatMessage({
                                        defaultMessage: 'Open AR view',
                                        id: 'product_detail.button.open_ar_view'
                                    })}
                                >
                                    <span>
                                        <FormattedMessage
                                            id="product_detail.button.view_in_space"
                                            defaultMessage="View in space"
                                        />
                                    </span>
                                    <_3DViewIcon role="presentation" />
                                </Button>
                            )}
                        </VStack>

                        <VStack
                            gap="spacing-16"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                            paddingBlock="spacing-24"
                            backgroundColor="primary.base.white"
                            borderTop="1px solid"
                            borderTopColor="border.subtle"
                            backdropFilter="auto"
                            backdropBlur="md"
                        >
                            <HStack justify="space-between" align="flex-end">
                                <ProductDisplayPrice
                                    as={DisplayPrice}
                                    product={product}
                                    loading={
                                        isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                    }
                                    showVatLabel={true}
                                />
                                {!isProductSet(product) && viewModel.ecommerceEnabled && (
                                    <ProductAvailability
                                        loading={isVariantLoading}
                                        flexShrink="0"
                                        product={product}
                                    />
                                )}
                            </HStack>
                            <CockpitCtaSection viewModel={viewModel} />
                            <EstimatedDelivery product={product}></EstimatedDelivery>
                        </VStack>
                    </VStack>
                </Box>
            </Box>
        </Box>
    )
}

XleapCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    onRatingsClick: PropTypes.func,
    viewModel: PropTypes.object.isRequired
}

export default XleapCockpit
